import { ethers } from 'ethers';
import Web3Modal, { CHAIN_DATA_LIST, ChainData } from 'web3modal';
import WalletConnect from '@walletconnect/web3-provider';
import CoinbaseWalletSDK from '@coinbase/wallet-sdk';
import { CoinbaseWalletSDKOptions } from '@coinbase/wallet-sdk/dist/CoinbaseWalletSDK';
import Torus from '@toruslabs/torus-embed';
import { useEffect, useRef, useState } from 'react';
import { useSetState } from 'react-use';
import truncateEthAddress from 'truncate-eth-address';
import omit from 'lodash/omit';
import Router from 'next/router';
import {
  Box,
  Button,
  Stack,
  HStack,
  useToast,
  Text,
  Popover,
  PopoverTrigger,
  PopoverArrow,
  PopoverContent,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  Input,
} from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';
import copy from 'copy-to-clipboard';
import { createOrder } from '@/services/order';
import {
  DEPOSIT_ADDRESS,
  DEPOSIT_CHAIN_ID,
  SkuCode,
  skuEthPriceMap,
} from '@lib/config';
import * as services from '@/services/login';
import * as deviceServices from '@/services/device';
import { MyLicense } from '@/components/Buy/MyLicense';
import { ethErrorCodeMap, isAddressEqual, isProd } from '@lib/utils';
import { generateMessage } from '@lib/signature';
import { SkuList } from '@/components/Buy/Sku';
import type { User } from '@prisma/client';
import type { TransactionReceipt } from '@ethersproject/providers';
import { handleTxResponse } from './handleTxResponse';
import { useIntl } from 'react-intl';

const infuraId = '0ade1eee56c941f8a0cdafa4d35f8588';

const providerOptions = {
  walletconnect: {
    package: WalletConnect,
    options: {
      infuraId,
      rpc: {
        56: 'https://bscrpc.com', // Binance Smart Chain
        97: 'https://data-seed-prebsc-2-s3.binance.org:8545', // Binance Smart Chain Testnet
        137: 'https://rpc-mainnet.matic.network', // Polygon
        42161: 'https://arb1.arbitrum.io/rpc', // Arbitrum One
        421611: 'https://rinkeby.arbitrum.io/rpc', // Arbitrum Rinkeby
        43114: 'https://api.avax.network/ext/bc/C/rpc', // Avalanche C-Chain
      },
    },
  },
  walletlink: {
    package: CoinbaseWalletSDK,
    options: {
      appName: 'Discord Superman',
      appLogoUrl:
        'https://discordsuperman.xyz/assets/discord-superman-logo.png',
      infuraId,
    } as CoinbaseWalletSDKOptions,
  },
  torus: {
    package: Torus,
  },
};

// 仅用于排查：注入钱包自报的标识。很多钱包会同时声明 isMetaMask
function getInjectedWalletFlags(): string {
  try {
    const eth = (window as any).ethereum;
    const keys = new Set<string>();
    for (let obj = eth; obj && obj !== Object.prototype; ) {
      Object.getOwnPropertyNames(obj).forEach((key) => keys.add(key));
      obj = Object.getPrototypeOf(obj);
    }
    return Array.from(keys)
      .filter((key) => /^is[A-Z]/.test(key) && eth[key] === true)
      .join(',');
  } catch {
    return '';
  }
}

interface UserMeta {
  publicAddress: string | null;
}

const initialMeta: UserMeta = { publicAddress: '' };
const defaultChain: ChainData = CHAIN_DATA_LIST[DEPOSIT_CHAIN_ID];

export function Purchase(props: { referralCode?: string }) {
  const [meta, setMeta] = useSetState<UserMeta>(initialMeta);
  const [chain, setChain] = useState<ChainData>(defaultChain);

  const signerRef = useRef<ethers.Signer | null>(null);
  const web3ModalRef = useRef<Web3Modal | null>(null);
  const [txProcessing, setTxProcessing] = useState(false);
  const [activeSku, setActiveSku] = useState<SkuCode | null>(null);
  const toast = useToast({ position: 'top', duration: 3000, isClosable: true });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const intl = useIntl();

  useEffect(() => {
    const web3Modal = new Web3Modal({
      network: '', // optional
      cacheProvider: true, // optional
      providerOptions, // required
    });
    web3ModalRef.current = web3Modal;
    if (web3Modal.cachedProvider) {
      // 如果有上次使用的钱包，自动尝试连接钱包
      onConnectWallet();
    } else {
      // 如果没有上次使用的钱包，则让用户手动连接钱包
    }
  }, []);

  useEffect(() => {
    const confirmationMessage = intl.formatMessage({
      defaultMessage: '交易正在进行中，现在离开会导致交易失败，确定离开吗?',
    });
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      (e || window.event).returnValue = confirmationMessage;
      return confirmationMessage; // Gecko + Webkit, Safari, Chrome etc.
    };
    const beforeRouteHandler = (url: string) => {
      if (Router.pathname !== url && !confirm(confirmationMessage)) {
        // to inform NProgress or something ...
        Router.events.emit('routeChangeError');
        // tslint:disable-next-line: no-string-throw
        throw `Route change to "${url}" was aborted (this error can be safely ignored). See https://github.com/zeit/next.js/issues/2476.`;
      }
    };
    if (txProcessing) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
      Router.events.on('routeChangeStart', beforeRouteHandler);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      Router.events.off('routeChangeStart', beforeRouteHandler);
    }
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      Router.events.off('routeChangeStart', beforeRouteHandler);
    };
  }, [txProcessing]);

  useEffect(() => {
    if (!meta.publicAddress) {
      return;
    }
    services
      .getUser({ publicAddress: meta.publicAddress })
      .then((res) => {
        if (res.success) {
          setIsLoggedIn(true);
          setUser(res.payload!);
        }
      })
      .catch((err) => {
        // console.log(err);
      });
  }, [meta.publicAddress]);

  async function connectWallet() {
    const web3Modal = web3ModalRef.current!;
    if (!web3Modal) {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'web3Modal 不存在',
          id: 'purchase.web3modal_not_exist',
        }),
        status: 'error',
      });
      return;
    }

    const provider = await web3Modal.connect();
    await provider.enable();

    // The "any" network will allow spontaneous network changes
    const web3Provider = new ethers.providers.Web3Provider(provider, 'any');

    // The MetaMask plugin also allows signing transactions to
    // send ether and pay to change state within the blockchain.
    // For this, you need the account signer...
    const signer = web3Provider.getSigner();
    signerRef.current = signer;

    const [publicAddress, chainId] = await Promise.all([
      signer.getAddress(),
      signer.getChainId(),
    ]);

    web3Provider.on('network', (newNetwork, oldNetwork) => {
      // When a Provider makes its initial connection, it emits a "network"
      // event with a null oldNetwork along with the newNetwork. So, if the
      // oldNetwork exists, it represents a changing network
      if (oldNetwork) {
        toast({
          title: intl.formatMessage({
            defaultMessage: '网络切换成功',
            id: 'purchase.network_switch_success',
          }),
        });
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    });

    // Subscribe to accounts change
    provider.on('accountsChanged', (accounts: string[]) => {
      toast({
        title: intl.formatMessage(
          {
            defaultMessage: `账户切换为 {account}`,
          },
          {
            account: accounts[0],
          },
        ),
      });
      setTimeout(() => {
        window.location.reload();
      }, 300);
    });

    // Subscribe to web3Provider connection
    provider.on('connect', (info: { chainId: number }) => {
      console.log(info);
    });

    return { publicAddress, chainId };
  }

  // ethers 用钱包当前账户签名和转账，它可能已不是页面显示的账户
  async function assertAccountUnchanged(signer: ethers.Signer) {
    const address = await signer.getAddress();
    if (!isAddressEqual(address, meta.publicAddress!)) {
      setTimeout(() => {
        window.location.reload();
      }, 300);
      throw {
        msg: intl.formatMessage(
          {
            defaultMessage: `账户切换为 {account}`,
          },
          {
            account: address,
          },
        ),
      };
    }
  }

  async function ssoLogin() {
    const signer = signerRef.current;
    if (!signer) {
      toast({ title: intl.formatMessage({ defaultMessage: '请先连接钱包' }) });
      return false;
    }
    toast({
      title: intl.formatMessage({
        defaultMessage: '正在校验身份',
      }),
      description: intl.formatMessage({
        defaultMessage: '请打开钱包确认签名...',
      }),
    });
    await assertAccountUnchanged(signer);
    const message = generateMessage(meta.publicAddress!);
    const signature = await signer.signMessage(message);

    const res = await services.login({
      publicAddress: meta.publicAddress!,
      message,
      signature,
      wallet: `${
        web3ModalRef.current?.cachedProvider
      }:${getInjectedWalletFlags()}`,
    });

    if (res.success) {
      toast({
        title: intl.formatMessage({ defaultMessage: '登录成功' }),
        status: 'success',
      });
      setIsLoggedIn(true);
      setUser(res.payload!);
      return true;
    } else {
      throw res;
    }
  }

  function onConnectWallet() {
    if (meta.publicAddress) {
      return;
    }
    return connectWallet()
      .then((meta) => {
        if (meta) {
          setMeta({ publicAddress: meta.publicAddress });
          setChain(CHAIN_DATA_LIST[meta.chainId!] || defaultChain);
        } else {
          toast({
            title: intl.formatMessage({ defaultMessage: '没有获取到钱包信息' }),
          });
        }
      })
      .catch((err) => {
        localStorage.removeItem('walletconnect');
        web3ModalRef.current?.clearCachedProvider();
        toast({
          title: intl.formatMessage({ defaultMessage: '连接钱包出现问题' }),
          description: ethErrorCodeMap[err.code] || err.reason || err.message,
          status: 'error',
        });
      });
  }

  function clearWalletConnect() {
    localStorage.removeItem('walletconnect');
    web3ModalRef.current?.clearCachedProvider();
  }

  function disconnectWallet() {
    signerRef.current = null;
    setMeta(initialMeta);
    setChain(defaultChain);
    clearWalletConnect();
  }

  async function onReceipt(receipt: TransactionReceipt, sku: SkuCode) {
    return createOrder({
      receipt: omit(receipt, 'logs'),
      skuCode: sku,
      referralCode: props.referralCode,
    })
      .then((res) => {
        if (res.success) {
          toast({
            title: intl.formatMessage({ defaultMessage: '购买成功' }),
            description: intl.formatMessage({
              defaultMessage: '稍后页面将自动刷新',
            }),
            status: 'success',
          });
          setTimeout(() => {
            if (isProd) {
              window.location.reload();
            } else {
              console.log('[dev] 执行到页面刷新');
            }
          }, 1500);
        } else {
          toast({
            title: intl.formatMessage({ defaultMessage: '订单创建失败' }),
            description: res.msg,
            status: 'error',
          });
        }
      })
      .catch((err) => {
        toast({
          title: intl.formatMessage({ defaultMessage: '订单创建出现异常' }),
          description: err.message,
          status: 'error',
        });
      });
  }

  async function onBuy(sku: SkuCode) {
    const signer = signerRef.current;
    if (!signer) {
      toast({ title: intl.formatMessage({ defaultMessage: '请先连接钱包' }) });
      return;
    }
    try {
      await assertAccountUnchanged(signer);
    } catch (err: any) {
      toast({ title: err.msg || err.message, status: 'error' });
      return;
    }
    const chainId = chain.chainId;
    if (isProd) {
      if (chainId !== 1) {
        toast({
          title: intl.formatMessage({
            defaultMessage: '请切换到以太坊主网（Ethereum Mainnet）',
          }),
        });
        if (window.ethereum.request) {
          try {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: '0x1' }],
            });
          } catch (err) {
            console.error('切换网络失败，请手动切换', err);
          }
        }
        return;
      } else {
        // 继续执行后续逻辑
      }
    } else {
      if (chainId !== 4) {
        toast({
          title: intl.formatMessage({
            defaultMessage: '当前非测试网，请留意!',
          }),
          status: 'warning',
        });
        toast({
          title: intl.formatMessage({
            defaultMessage: '当前非测试网，请留意!',
          }),
          status: 'warning',
        });
        toast({
          title: intl.formatMessage({
            defaultMessage: '当前非测试网，请留意!',
          }),
          status: 'warning',
        });
      } else {
        // 继续执行后续逻辑
      }
    }

    setActiveSku(sku);
    setTxProcessing(true);

    if (!isLoggedIn) {
      try {
        await ssoLogin();
      } catch (err: any) {
        toast({
          title: intl.formatMessage({ defaultMessage: '登录过程出现异常' }),
          description:
            ethErrorCodeMap[err.code] || err.reason || err.message || err.msg,
          status: 'error',
        });
        setActiveSku(null);
        setTxProcessing(false);
        return;
      }
    }
    toast({
      title: intl.formatMessage({ defaultMessage: '开始发送交易' }),
      description: intl.formatMessage({
        defaultMessage: '请打开钱包确认交易...',
      }),
    });
    const data = ethers.utils.toUtf8Bytes(
      JSON.stringify({
        sku,
        referralCode:
          props.referralCode === user?.referralCode
            ? undefined
            : props.referralCode,
      }),
    );
    signerRef
      .current!.sendTransaction({
        chainId: DEPOSIT_CHAIN_ID, // 实际使用的链是由用户的钱包决定的，所以在发送交易前要前检查用户当前的链
        to: DEPOSIT_ADDRESS,
        value: ethers.utils.parseEther(skuEthPriceMap[sku].toString()),
        // data: ethers.utils.hexValue(data), // 产生 “External transactions to internal accounts cannot include data (-32602)” 这个 RPC 错误的代码改动，最早由 MetaMask 核心仓库 PR #5418 引入，合并时间为 2025 年 3 月 11 日
      })
      .then((tx) => {
        return handleTxResponse(intl, tx, toast).then((receipt) => {
          if (receipt) {
            return onReceipt(receipt, sku);
          } else {
            // 说明出现错误，这里不再做处理
          }
        });
      })
      .catch((err) => {
        const code: string | number = err.code;
        toast({
          title: intl.formatMessage({ defaultMessage: '发送交易失败' }),
          description: ethErrorCodeMap[code] || err.reason || err.message,
          status: 'error',
        });
      })
      .finally(() => {
        setTxProcessing(false);
        setActiveSku(null);
      });
  }

  async function onLogin() {
    try {
      await ssoLogin();
      return true;
    } catch (err: any) {
      toast({
        title: intl.formatMessage({ defaultMessage: '登录过程出现异常' }),
        description:
          ethErrorCodeMap[err.code] || err.reason || err.message || err.msg,
        status: 'error',
      });
      return false;
    }
  }

  async function onUnbindDevice(licenseCode: string) {
    const confirmed = window.confirm(
      intl.formatMessage({
        defaultMessage:
          '确定要解绑所有设备吗？请勿在短时间内频繁换绑设备，否则会被系统认定为滥用',
      }),
    );
    if (!confirmed) {
      return;
    }

    const signer = signerRef.current;
    if (!signer) {
      toast({ title: intl.formatMessage({ defaultMessage: '请先连接钱包' }) });
      return;
    }

    toast({
      title: intl.formatMessage({ defaultMessage: '请打开钱包确认签名...' }),
    });
    const message = generateMessage(meta.publicAddress!);
    const signature = await signer.signMessage(message);

    try {
      const res = await deviceServices.unbindAllDevice({
        publicAddress: meta.publicAddress!,
        license: licenseCode,
        signature,
        message,
      });

      if (res.success) {
        toast({
          title: intl.formatMessage({ defaultMessage: '解绑成功' }),
          description: intl.formatMessage({
            defaultMessage: '现在可以在其他设备上绑定了',
          }),
          status: 'success',
        });
      } else {
        toast({
          title: intl.formatMessage({ defaultMessage: '解绑失败' }),
          description: res.msg,
          status: 'error',
        });
      }
    } catch (err: any) {
      toast({
        title: intl.formatMessage({ defaultMessage: '解绑设备出现异常错误' }),
        description: err.message,
        status: 'error',
      });
    }
  }

  return (
    <Stack margin="auto" mt={10} gap={8} p={1} pb={10}>
      <Box>
        <Popover isLazy trigger="click" defaultIsOpen={false} size="xs">
          <PopoverTrigger>
            <Button colorScheme="blue" onClick={onConnectWallet} isFullWidth>
              {meta.publicAddress
                ? `${truncateEthAddress(meta.publicAddress)} (${chain?.chain} ${
                    chain?.network
                  })`
                : intl.formatMessage({ defaultMessage: '连接钱包' })}
            </Button>
          </PopoverTrigger>
          {meta.publicAddress && (
            <PopoverContent>
              <PopoverHeader
                fontWeight="semibold"
                textAlign="center"
                id="disconnect-wallet-title"
              >
                {intl.formatMessage({
                  defaultMessage: '确定要断开钱包连接吗？',
                })}
              </PopoverHeader>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody justifyContent="center" display="flex">
                <Button
                  colorScheme="red"
                  onClick={disconnectWallet}
                  isFullWidth
                >
                  {intl.formatMessage({ defaultMessage: '断开钱包' })}
                </Button>
              </PopoverBody>
            </PopoverContent>
          )}
        </Popover>
      </Box>
      <SkuList onBuy={onBuy} activeSku={activeSku} isLoading={txProcessing} />
      {isLoggedIn && (
        <Stack gap={4}>
          <Stack mt={4}>
            <HStack>
              <Text>
                {intl.formatMessage({ defaultMessage: '邀请返利链接' })}
              </Text>
              <Text color="gray" fontSize="sm">
                （
                {intl.formatMessage(
                  {
                    defaultMessage:
                      '当前累积奖励 {pendingBonus} U，已提现 {totalPaidBonus} U',
                  },
                  {
                    pendingBonus: user?.pendingBonus || 0,
                    totalPaidBonus: user?.totalPaidBonus || 0,
                  },
                )}
                ）
              </Text>
            </HStack>
            <HStack>
              <Input
                value={`https://discordsuperman.xyz/buy/${
                  user?.referralCode || ''
                }`}
                onChange={() => {}}
                onClick={(evt) => {
                  const target = evt.target as HTMLInputElement;
                  const value = target.value;
                  copy(value);
                  toast({
                    title: intl.formatMessage({
                      defaultMessage: '邀请链接已复制',
                    }),
                    description: intl.formatMessage({
                      defaultMessage: '快将邀请链接发送给好友赚取丰厚返利吧',
                    }),
                  });
                }}
                id="_invite_link"
                cursor="copy"
                size="sm"
              />
              <span
                onClick={() => {
                  const input = document.getElementById(
                    '_invite_link',
                  ) as HTMLInputElement;
                  input?.click();
                }}
                style={{ cursor: 'pointer' }}
              >
                <CopyIcon />
              </span>
            </HStack>
            <Text fontSize="sm" color="gray">
              {intl.formatMessage({
                defaultMessage:
                  '其他人通过此链接完成交易，你会获得 15% 的返利，系统在每月初自动提现到你的钱包。默认用 BEP20 网络转 USDT/USDC/BUSD 到你的钱包。如有特殊要求可进入 Discord 联系管理员或发邮件给我们',
              })}
            </Text>
          </Stack>
          <MyLicense
            isLoggedIn={isLoggedIn}
            publicAddress={meta.publicAddress!}
            onUnbindDevice={onUnbindDevice}
          />
        </Stack>
      )}
      {!isLoggedIn && (
        <Stack>
          <Button onClick={onLogin}>
            {intl.formatMessage({ defaultMessage: '账户信息' })}
          </Button>
          <Button onClick={onLogin}>
            {intl.formatMessage({ defaultMessage: '邀请返利' })}
          </Button>
        </Stack>
      )}
      <Box>
        <Text color="gray" fontSize="sm">
          {intl.formatMessage({
            defaultMessage:
              '提示：如果你想节省 GAS，可以直接在 L2(OP,Arbitrum,Base) 上手动转账等额的 ETH 到 `0xcBf641a213E742f60DEBa3bF5ae4ee87cA295Bd5`，然后邮件通知我们手动帮你开通。我们的邮箱地址：`hi@discordsuperman.xyz`',
            id: 'purchase.l2_transfer_tip',
          })}
        </Text>
      </Box>
    </Stack>
  );
}
